import { useEffect, useRef, useState } from 'react';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Animates a number from 0 up to `target` once the value is known.
 *
 * Eases out so the count decelerates rather than ticking linearly, and skips
 * straight to the final value when the viewer has asked for reduced motion.
 */
const useCountUp = (target, duration = 900) => {
  const [display, setDisplay] = useState(0);
  const frameRef = useRef(null);

  useEffect(() => {
    const end = Number(target);

    if (!Number.isFinite(end)) {
      setDisplay(0);
      return undefined;
    }

    if (prefersReducedMotion() || end === 0) {
      setDisplay(end);
      return undefined;
    }

    const start = performance.now();

    const tick = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(end * eased);

      if (progress < 1) {
        frameRef.current = requestAnimationFrame(tick);
      } else {
        setDisplay(end);
      }
    };

    frameRef.current = requestAnimationFrame(tick);

    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
  }, [target, duration]);

  return display;
};

export default useCountUp;
